import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol
} from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { environment } from '@app/environment';
import { MarvColorPrimary } from '@marv/types';
import { MarvInputComponent, MarvButtonComponent, MarvSelectItem, MarvSelectExternalSource } from '@marv/components';
import { StoreSelectComponent, RetailerSelectComponent, AreaSelectComponent, SectorSelectComponent, OperationSelectComponent } from '@app/components';
import { OperationService, CustomerSectorService, RetailerService, StoreService } from '@app/services';
import { OperationCard } from '@app/models';

export class StoreFiltersValues {
    
    storesCodes: Array<string>;
    city: string;
    retailersCodes: Array<string>;
    areasIds: Array<number>;
    sectorsIds: Array<number>;
    withOperationId: number;
    withoutOperationId: number;
    nbFiltersFilled: number = 0;
    
    static FromForm(form: FormGroup): StoreFiltersValues{
        let filters = new StoreFiltersValues();
        if(form.value?.storesCodes && form.value.storesCodes.length > 0){
            filters.storesCodes = form.value.storesCodes;
            filters.nbFiltersFilled++;
        }
        if(form.value?.city && form.value.city != ''){
            filters.city = form.value.city;
            filters.nbFiltersFilled++;
        }
        if(form.value?.retailersCodes && form.value.retailersCodes.length > 0){
            filters.retailersCodes = form.value.retailersCodes;
            filters.nbFiltersFilled++;
        }
        if(form.value?.areasIds && form.value.areasIds.length > 0){
            filters.areasIds = form.value.areasIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.sectorsIds && form.value.sectorsIds.length > 0){
            filters.sectorsIds = form.value.sectorsIds;
            filters.nbFiltersFilled++;
        }
        if(form.value?.withOperationId && form.value.withOperationId != ''){
            filters.withOperationId = form.value.withOperationId;
            filters.nbFiltersFilled++;
        }
        if(form.value?.withoutOperationId && form.value.withoutOperationId != ''){
            filters.withoutOperationId = form.value.withoutOperationId;
            filters.nbFiltersFilled++;
        }
        return filters;
    }
}

@Component({
    selector: 'app-store-filters',
    templateUrl: './filters.component.html',
    styleUrls: ['./filters.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, TranslatePipe, ReactiveFormsModule, 
        forwardRef(() => MarvInputComponent), forwardRef(() => MarvButtonComponent), 
        forwardRef(() => StoreSelectComponent), forwardRef(() => RetailerSelectComponent), forwardRef(() => AreaSelectComponent),
        forwardRef(() => SectorSelectComponent), forwardRef(() => OperationSelectComponent)
    ]
})
export class StoreFiltersComponent  implements OnInit {

    @Input({alias : 'operations', required : true}) operations: Array<OperationCard> = [];
    @Input({alias : 'storeFiltersValues', required : false}) storeFiltersValues: StoreFiltersValues = new StoreFiltersValues();
    @Output() onChangeStoreFilters = new EventEmitter<StoreFiltersValues>();

    filtersStoreForm: FormGroup = new FormGroup({
        storesCodes: new FormControl([]),
        city: new FormControl(''),
        retailersCodes: new FormControl([]),
        areasIds: new FormControl([]),
        sectorsIds: new FormControl([]),
        withOperationId: new FormControl(''),
        withoutOperationId: new FormControl(''),
    });
    externalSourceRetailers: MarvSelectExternalSource|undefined;
    stores: Array<MarvSelectItem> = [];
    externalSourceStores: MarvSelectExternalSource|undefined;
    sectors: Array<MarvSelectItem> = [];
    hideSectors: boolean = false;
    updateRetailersSubscription: Subscription;
    updateAreasSubscription: Subscription;
    
    constructor() { }

    ngOnInit() {
        
        this.filterStores();
        this.updateRetailersSubscription = this.filtersStoreForm.controls['retailersCodes'].valueChanges.subscribe(value => {
            this.filterStores();
        }); 
        
        if(!RetailerService.Retailers || RetailerService.Retailers.length === 0){
            this.initExternalSourceRetailers();
        }
        
        if(StoreService.Stores && StoreService.Stores.length > 0){
            this.filterSectors();        
            this.updateAreasSubscription = this.filtersStoreForm.controls['areasIds'].valueChanges.subscribe(value => {
                this.filterSectors();
            });  
        }
        else{
            this.initExternalSourceStores();
        }      
    }
    
    ngOnDestroy() {
        if(this.updateAreasSubscription){
            this.updateAreasSubscription.unsubscribe();
        }
    }
    
    async ngOnChanges(changes: SimpleChanges) {
                
        if(changes['operations'] && changes['operations'].currentValue){     
        
            let operationsIds: Array<number> = [];
            let permanentsOperationsIds: Array<number> = [];
            
            this.operations.filter(o => o.type === 'loga').forEach(o => {
                operationsIds.push(o.id);
            });
            
            this.operations.filter(o => o.type === 'lgg').forEach(o => {
                permanentsOperationsIds.push(o.id);
            });        
            
        }
        
        if(changes['storeFiltersValues'] && changes['storeFiltersValues'].currentValue){
            let values = {
                storesCodes: (this.storeFiltersValues.storesCodes?.length > 0 ? this.storeFiltersValues.storesCodes : []),
                city: (this.storeFiltersValues.city && this.storeFiltersValues.city != '' ? this.storeFiltersValues.city : ''),
                retailersCodes: (this.storeFiltersValues.retailersCodes?.length > 0 ? this.storeFiltersValues.retailersCodes : []),
                areasIds: (this.storeFiltersValues.areasIds?.length > 0 ? this.storeFiltersValues.areasIds : []),
                sectorsIds: (this.storeFiltersValues.sectorsIds?.length > 0 ? this.storeFiltersValues.sectorsIds : []),
                withOperationId: (Number.isInteger(this.storeFiltersValues.withOperationId) ? this.storeFiltersValues.withOperationId : ''),
                withoutOperationId: (Number.isInteger(this.storeFiltersValues.withoutOperationId) ? this.storeFiltersValues.withoutOperationId : ''),
            };  
            this.filtersStoreForm.setValue(values);
        }
        
    }
    
    initExternalSourceRetailers(){
        let opIds = this.parseOperationsIds();
        this.externalSourceRetailers = {
            url: 'retailer',
            params : {
                opIDs: opIds.operationsIds,
                ctxIDs: opIds.contextsIds
            },
            labelField: 'name',
            valueField: 'code',
            transformItem: (json: any): MarvSelectItem => {                    
                return {
                    label: json['name'],
                    value: json['code'],
                    color: MarvColorPrimary,
                    picture: environment.URL_LOGO_RETAILER.replace('#retailer_name#', json['name'])
                };
            }
        };
    }
    
    initExternalSourceStores(){
        let opIds = this.parseOperationsIds();
        this.externalSourceStores = {
            url: 'store',
            params : {
                opIDs: opIds.operationsIds,
                ctxIDs: opIds.contextsIds
            },
            labelField: 'name',
            valueField: 'code',
            transformItem: (json: any): MarvSelectItem => {                    
                return {
                    label: json['name'],
                    value: json['code'],
                    color: MarvColorPrimary
                };
            }
        };
    }
    
    parseOperationsIds(){
        let operationsIds: Array<number> = [];          
        let contextsIds: Array<number> = [];  
        OperationService.Operations.forEach(o => {
            if(o.type === 'loga'){
                operationsIds.push(o.id);
            }
            else if  (o.type === 'lgg'){
                contextsIds.push(o.id);
            }           
        });        
        return {operationsIds: operationsIds, contextsIds: contextsIds};
    }
    
    filterStores(){
        let stores: Array<MarvSelectItem> = [];
        let retailersCodes = this.filtersStoreForm.controls['retailersCodes'].value;
        let storesCodes = this.filtersStoreForm.controls['storesCodes'].value;
        for(let s of StoreService.Stores){
            if((!retailersCodes || retailersCodes.length === 0) || retailersCodes.indexOf(s.retailer.code) !== -1){
                stores.push(new MarvSelectItem(s.name, s.code, environment.URL_LOGO_RETAILER.replace('#retailer_name#', s.retailer.name)));
            }
            else{
                let indexSector = storesCodes.indexOf(s.code)
                if(indexSector !== -1){
                    storesCodes.splice(indexSector, 1);
                }
            }
        }   
        this.filtersStoreForm.controls['storesCodes'].setValue(storesCodes, {emitEvent: false}); 
        this.stores = stores;
    }
    
    filterSectors(){        
        let sectors: Array<MarvSelectItem> = [];
        let areasIds = this.filtersStoreForm.controls['areasIds'].value;
        let sectorsIds = this.filtersStoreForm.controls['sectorsIds'].value;
        if(CustomerSectorService.Sectors && CustomerSectorService.Sectors.length > 0){
            this.hideSectors = false;
            for(let s of CustomerSectorService.Sectors){
                if((!areasIds || areasIds.length === 0) || areasIds.indexOf(s.area?.id) !== -1){
                    sectors.push(new MarvSelectItem(s.name, s.id));
                }
                else{
                    let indexSector = sectorsIds.indexOf(s.id)
                    if(indexSector !== -1){
                        sectorsIds.splice(indexSector, 1);
                    }
                }
            }        
        }
        else{
            this.hideSectors = true;
        }
        this.sectors = sectors;
    }
    
    search() {
        if(this.filtersStoreForm.valid){            
            this.onChangeStoreFilters.emit(StoreFiltersValues.FromForm(this.filtersStoreForm));
        }
        else{
            for (var controlName in this.filtersStoreForm.controls) {
                if(!this.filtersStoreForm.controls[controlName].valid) {
                    this.filtersStoreForm.controls[controlName].markAsTouched();
                }
            }
        }
    }
    
    displayAll() {
        this.filtersStoreForm.reset();
        let filters = new StoreFiltersValues();
        this.onChangeStoreFilters.emit(filters);
    }
    
}
